import React, { useEffect } from 'react';
import { Phone, Smartphone, Mail, MapPin, Activity, Heart, Brain, Footprints, Bone, Syringe, Bike, Wind, Target, Sparkles, TrendingUp, Users, Shield, Dumbbell, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import ChatBot from '../components/ChatBot';
import LungsIcon from '../components/icons/LungsIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  // Track page visit
  useEffect(() => {
    const trackVisit = async () => {
      try {
        // Get or create visitor ID
        let visitorId = localStorage.getItem('visitor_id');
        if (!visitorId) {
          visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('visitor_id', visitorId);
        }
        
        await fetch(`${BACKEND_URL}/api/analytics/visit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ page: 'home', visitor_id: visitorId })
        });
      } catch (error) {
        console.log('Analytics tracking failed');
      }
    };
    
    trackVisit();
  }, []);

  const contactInfo = {
    phone: '+32 58 31 31 34',
    mobile: '+32 476 51 08 89',
    email: 'julien@kineryckeboer.be',
    address: 'Edmond Ronselaan 8, 8630 Veurne'
  };

  const domeinen = [
    { title: 'Algemene kinesitherapie', subtitle: null },
    { title: 'Sportkinesitherapie', subtitle: 'wielrennen - schaatsen - skeeleren - sportadvies' },
    { title: 'Orthopedische revalidatie', subtitle: null },
    { title: 'Cardiorevalidatie', subtitle: null },
    { title: 'Pneumo revalidatie', subtitle: null },
    { title: 'Gangrevalidatie en valpreventie', subtitle: null },
    { title: 'Musculoskeletale revalidatie', subtitle: null },
    { title: 'Neuro revalidatie', subtitle: null },
    { title: 'Dry Needling', subtitle: null },
    { title: 'Bikefitting', subtitle: 'fietsafstelling op maat' },
    { title: 'Verbeteren van de levenskwaliteit', subtitle: null }
  ];

  const focus = [
    'Functionele revalidatie',
    'Bewegingskwaliteit',
    'Herstel',
    'Goed gevoel',
    'Belastbaarheid versus belasting',
    'Persoonlijke begeleiding en advies'
  ];

  const handlePhoneClick = (number) => {
    window.location.href = `tel:${number}`;
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${contactInfo.email}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex justify-between items-center">
            <div>
              <div className="text-2xl font-bold text-primary-dark">
                Kine Julien Ryckeboer
              </div>
              <div className="text-sm text-gray-500">
                Master LO en Kinesitherapie
              </div>
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="#home" className="nav-link">Home</a>
              <a href="#profiel" className="nav-link">Profiel</a>
              <a href="#domeinen" className="nav-link">Domeinen</a>
              <a href="#focus" className="nav-link">Focus</a>
              <a href="#contact" className="nav-link">Contact</a>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section - Pag 1 */}
      <section id="home" className="hero-section">
        <div className="hero-overlay"></div>
        <div className="container mx-auto px-4 py-32 relative z-10">
          <div className="max-w-4xl mx-auto text-white text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Kinesitherapie in Veurne
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              Kinesitherapie voor revalidatie, beweging en comfort
            </p>
            
            {/* Dynamic buttons - show number, on hover show "Bel nu" */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6 justify-center">
              <Button 
                size="lg"
                onClick={() => handlePhoneClick(contactInfo.phone)}
                className="group relative bg-primary-green hover:bg-primary-green-dark text-white text-lg px-8 py-6 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl overflow-hidden min-w-[200px]"
              >
                <span className="flex items-center justify-center transition-all duration-300 group-hover:opacity-0 group-hover:-translate-y-full">
                  <Phone className="mr-2 h-5 w-5" />
                  058 31 31 34
                </span>
                <span className="absolute inset-0 flex items-center justify-center transition-all duration-300 opacity-0 translate-y-full group-hover:opacity-100 group-hover:translate-y-0">
                  <Phone className="mr-2 h-5 w-5" />
                  Bel nu
                </span>
              </Button>
              <Button 
                size="lg"
                onClick={() => handlePhoneClick(contactInfo.mobile)}
                className="group relative bg-primary-blue hover:bg-primary-dark text-white text-lg px-8 py-6 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl overflow-hidden min-w-[200px]"
              >
                <span className="flex items-center justify-center transition-all duration-300 group-hover:opacity-0 group-hover:-translate-y-full">
                  <Smartphone className="mr-2 h-5 w-5" />
                  0476 51 08 89
                </span>
                <span className="absolute inset-0 flex items-center justify-center transition-all duration-300 opacity-0 translate-y-full group-hover:opacity-100 group-hover:translate-y-0">
                  <Smartphone className="mr-2 h-5 w-5" />
                  Bel nu
                </span>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Profiel Section - Pag 2 */}
      <section id="profiel" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="section-title">Profiel</h2>
          <div className="max-w-4xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="relative group">
                {/* Decorative background elements */}
                <div className="absolute -inset-4 bg-gradient-to-br from-primary-green/20 to-primary-blue/20 rounded-2xl transform rotate-3 group-hover:rotate-1 transition-transform duration-500"></div>
                <div className="absolute -inset-4 bg-gradient-to-tr from-primary-blue/10 to-primary-green/10 rounded-2xl transform -rotate-3 group-hover:-rotate-1 transition-transform duration-500"></div>
                
                {/* Main image container */}
                <div className="relative overflow-hidden rounded-xl shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_github-connect-66/artifacts/921s4raj_20260517_083708000_iOS.jpg"
                    alt="Julien Ryckeboer - Kinesitherapeut"
                    className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-primary-dark mb-4">
                  Kine Julien Ryckeboer
                </h3>
                <ul className="space-y-3 text-lg text-gray-700">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-primary-green rounded-full mr-3"></span>
                    Master LO en kinesitherapie
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-primary-green rounded-full mr-3"></span>
                    Geregistreerd Sportkinesitherapeut
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-primary-green rounded-full mr-3"></span>
                    Behandeling in kabinet en aan huis
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Domeinen Section - Pag 2 */}
      <section id="domeinen" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="section-title">Domeinen</h2>
          <div className="max-w-5xl">
            {/* Dynamic Grid with Icons */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              
              <div className="group p-6 bg-gradient-to-br from-primary-green/5 to-primary-green/10 rounded-xl hover:from-primary-green/10 hover:to-primary-green/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <Activity className="h-8 w-8 text-primary-green mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Algemene kinesitherapie</h3>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-blue/5 to-primary-blue/10 rounded-xl hover:from-primary-blue/10 hover:to-primary-blue/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <Bike className="h-8 w-8 text-primary-blue mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Sportkinesitherapie</h3>
                <p className="text-xs text-gray-500 mt-1">wielrennen • schaatsen • skeeleren</p>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-green/5 to-primary-green/10 rounded-xl hover:from-primary-green/10 hover:to-primary-green/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <Bone className="h-8 w-8 text-primary-green mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Orthopedische revalidatie</h3>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-blue/5 to-primary-blue/10 rounded-xl hover:from-primary-blue/10 hover:to-primary-blue/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <Heart className="h-8 w-8 text-primary-blue mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Cardiorevalidatie</h3>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-green/5 to-primary-green/10 rounded-xl hover:from-primary-green/10 hover:to-primary-green/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <LungsIcon className="h-8 w-8 text-primary-green mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Pneumo revalidatie</h3>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-green/5 to-primary-green/10 rounded-xl hover:from-primary-green/10 hover:to-primary-green/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <Footprints className="h-8 w-8 text-primary-green mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Gangrevalidatie</h3>
                <p className="text-xs text-gray-500 mt-1">valpreventie</p>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-blue/5 to-primary-blue/10 rounded-xl hover:from-primary-blue/10 hover:to-primary-blue/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <Dumbbell className="h-8 w-8 text-primary-blue mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Musculoskeletale revalidatie</h3>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-green/5 to-primary-green/10 rounded-xl hover:from-primary-green/10 hover:to-primary-green/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <Brain className="h-8 w-8 text-primary-green mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Neuro revalidatie</h3>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-blue/5 to-primary-blue/10 rounded-xl hover:from-primary-blue/10 hover:to-primary-blue/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                {/* Custom Dry Needle SVG Icon */}
                <svg 
                  className="h-8 w-8 text-primary-blue mb-3 group-hover:scale-110 transition-transform" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  {/* Needle shaft */}
                  <line x1="4" y1="20" x2="18" y2="6" />
                  {/* Needle handle/grip */}
                  <circle cx="19.5" cy="4.5" r="2" />
                  {/* Small lines indicating trigger point */}
                  <line x1="7" y1="15" x2="5" y2="17" strokeWidth="1.5" />
                  <line x1="9" y1="13" x2="7" y2="15" strokeWidth="1.5" />
                </svg>
                <h3 className="font-semibold text-primary-dark text-sm">Dry Needling</h3>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-green/5 to-primary-green/10 rounded-xl hover:from-primary-green/10 hover:to-primary-green/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <Wrench className="h-8 w-8 text-primary-green mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Bikefitting</h3>
                <p className="text-xs text-gray-500 mt-1">fietsafstelling op maat</p>
              </div>

              <div className="col-span-2 md:col-span-3 lg:col-span-4 p-8 bg-gradient-to-r from-primary-green/5 via-primary-blue/5 to-primary-green/5 rounded-xl">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Sparkles className="h-6 w-6 text-primary-green" />
                  <h3 className="font-semibold text-primary-dark">Verbeteren van de levenskwaliteit</h3>
                  <Sparkles className="h-6 w-6 text-primary-blue" />
                </div>
                <p className="text-center italic text-gray-600 text-sm md:text-base tracking-wide">
                  &ldquo;Leven toevoegen aan de jaren, niet alleen jaren aan het leven<span className="text-primary-green">...</span>&rdquo;
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Focus Section - Pag 3 */}
      <section id="focus" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-title">Focus</h2>
          <div className="max-w-5xl">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              
              <div className="group p-6 bg-gradient-to-br from-primary-green/5 to-primary-green/10 rounded-xl hover:from-primary-green/10 hover:to-primary-green/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <Target className="h-8 w-8 text-primary-green mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Functionele revalidatie</h3>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-blue/5 to-primary-blue/10 rounded-xl hover:from-primary-blue/10 hover:to-primary-blue/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <Activity className="h-8 w-8 text-primary-blue mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Bewegingskwaliteit</h3>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-green/5 to-primary-green/10 rounded-xl hover:from-primary-green/10 hover:to-primary-green/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <TrendingUp className="h-8 w-8 text-primary-green mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Herstel</h3>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-blue/5 to-primary-blue/10 rounded-xl hover:from-primary-blue/10 hover:to-primary-blue/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <Sparkles className="h-8 w-8 text-primary-blue mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Goed gevoel</h3>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-green/5 to-primary-green/10 rounded-xl hover:from-primary-green/10 hover:to-primary-green/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <Shield className="h-8 w-8 text-primary-green mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Belastbaarheid versus belasting</h3>
              </div>

              <div className="group p-6 bg-gradient-to-br from-primary-blue/5 to-primary-blue/10 rounded-xl hover:from-primary-blue/10 hover:to-primary-blue/20 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default">
                <Users className="h-8 w-8 text-primary-blue mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-primary-dark text-sm">Persoonlijke begeleiding en advies</h3>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Contact Section - Pag 4 */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-title">Contact & Afspraak</h2>
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl">
            <div>
              <Card className="p-6">
                <h3 className="text-2xl font-bold text-primary-dark mb-6">Contactgegevens</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <Phone className="h-6 w-6 text-primary-green mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-700">Vaste lijn</p>
                      <a 
                        href={`tel:${contactInfo.phone}`}
                        className="text-primary-blue hover:underline text-lg"
                      >
                        +32 58 31 31 34
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Smartphone className="h-6 w-6 text-primary-green mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-700">GSM</p>
                      <a 
                        href={`tel:${contactInfo.mobile}`}
                        className="text-primary-blue hover:underline text-lg"
                      >
                        +32 476 51 08 89
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Mail className="h-6 w-6 text-primary-green mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-700">E-mail</p>
                      <a 
                        href={`mailto:${contactInfo.email}`}
                        className="text-primary-blue hover:underline text-lg break-all"
                      >
                        julien@kineryckeboer.be
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="h-6 w-6 text-primary-green mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-700">Adres</p>
                      <p className="text-gray-600 text-lg">{contactInfo.address}</p>
                    </div>
                  </div>
                  <div className="pt-4">
                    <Button 
                      onClick={() => handlePhoneClick(contactInfo.mobile)}
                      className="w-full bg-primary-green hover:bg-primary-green-dark text-white text-lg py-6"
                    >
                      <Phone className="mr-2 h-5 w-5" />
                      Bel voor afspraak
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
            <div>
              <Card className="p-4 h-full">
                <iframe
                  title="Locatie Kine Julien Ryckeboer"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2497.8934!2d2.6584!3d51.0713!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47dcdb0c8a0b0001%3A0x1!2sEdmond%20Ronselaan%208%2C%208630%20Veurne!5e0!3m2!1snl!2sbe!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '400px', borderRadius: '0.5rem' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary-dark text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-2">Praktijk Kine Julien Ryckeboer</h3>
              <p className="text-gray-300 font-medium mb-1">
                Julien Ryckeboer
              </p>
              <p className="text-gray-400 text-sm mb-1">
                Master LO - kine
              </p>
              <p className="text-gray-400 text-sm">
                RIZIV-nummer: 1.5/09361/83/522
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Snelle Links</h4>
              <ul className="space-y-2">
                <li><a href="#home" className="text-gray-300 hover:text-primary-green transition-colors">Home</a></li>
                <li><a href="#profiel" className="text-gray-300 hover:text-primary-green transition-colors">Profiel</a></li>
                <li><a href="#domeinen" className="text-gray-300 hover:text-primary-green transition-colors">Domeinen</a></li>
                <li><a href="#focus" className="text-gray-300 hover:text-primary-green transition-colors">Focus</a></li>
                <li><a href="#contact" className="text-gray-300 hover:text-primary-green transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-300">
                <li>{contactInfo.address}</li>
                <li>+32 58 31 31 34</li>
                <li>+32 476 51 08 89</li>
                <li>{contactInfo.email}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Kine Julien Ryckeboer. Alle rechten voorbehouden.</p>
            <p className="text-xs text-gray-500 mt-2 flex items-center justify-center gap-1.5">
              <span>Made with</span>
              <Heart className="h-3.5 w-3.5 text-red-400 fill-red-400 animate-pulse" />
              <span>in Veurne</span>
            </p>
          </div>
        </div>
      </footer>

      {/* ChatBot */}
      <ChatBot />
    </div>
  );
};

export default Home;
